import uuidv4 from 'uuid/v4';
import _ from 'lodash';
import { has } from '../../../utils.js';
import basic from './common/basic.js';
import mention from './common/mention.js';
import boardTypes from '../types/boards-types.js';
import updater from '../updater.js';
import asset from './common/asset.js';

const options = {
  mentionType: 'board',
  container: { type: 'boards', prop: 'children' },
};

const state = { byId: {} };

const getters = {
  ...basic.getters(options),
  ...asset.getters(),
  ...mention.getters(options),

  /**
   * Return the board id that has the specific element and the index
   * @param {Object} state
   */
  getElementInBoards(state) {
    return id => {
      const b = Object.values(state.byId).find(
        board => has(board, 'elements') && board.elements.includes(id)
      );
      const index = state.byId[b.id].elements.indexOf(id);
      return { boardId: b.id, index };
    };
  },

  /**
   * Get all non-container boards in an array
   * @return {array}  The non container boards found
   */
  getNonContainerBoards(state) {
    return Object.values(state.byId).filter(item => !has(item, 'children'));
  },
};

const actions = {
  ...basic.actions({
    type: 'boards',
    container: { type: 'boards', prop: 'children' },
  }),

  /**
   * Create a new board as first child of root container.
   * @param   {NEW_ITEM|NEW_CONTAINER}  board     The default board
   * @param   {Object}                  props     Any extra or updated properties
   * @param   {Boolean}                 apply     Whether to apply the patch after finishing
   * @returns {String}                            The id of the board that was created
   */
  customCreate(
    context,
    { board, props = {}, tId = null, tIndex = 0, tExpand = false, apply = true }
  ) {
    if (tId && tExpand) {
      context.dispatch(
        'project/config/boards/setCollapsed',
        { id: tId, value: false, apply: false },
        { root: true }
      );
    }
    tId = tId || context.getters.getRootContainer.id;
    const id = uuidv4();
    context.dispatch('create', {
      node: {
        id,
        ...board.data,
        ...props,
      },
      tId,
      tIndex,
      apply: false,
    });
    // Create the default configuration for this board (other users should do this too)
    // context.dispatch(
    //   'project/config/boards/customCreate',
    //   { id, board, apply: false, ignore: false },
    //   { root: true }
    // );

    // Select it by default
    context.dispatch(
      'project/config/boards/deselectAll',
      { apply: false },
      { root: true }
    );
    context.dispatch(
      'project/config/boards/select',
      { id, apply: false },
      { root: true }
    );

    // Open it by default if it is not a container
    if (board === boardTypes.newItem) {
      context.dispatch(
        'project/config/boards/setOpen',
        { id, apply: false },
        { root: true }
      );
    }

    updater.complete({ apply });
    return Promise.resolve(id);
  },

  /**
   * Update some of the board's properties.
   * @param {String}  id      The id that should be updated
   * @param {Object}  props   Contains only the properties that are changed
   * @param {Boolean} apply   Whether to apply the patch after finishing
   */
  customUpdate(context, { id, props, apply }) {
    // Update the board's properties
    context.dispatch('update', { id, props, apply: false });
    // Update any mentions in elements, notes, connections, attributes
    if (has(props, 'name')) {
      ['elements', 'notes', 'connections', 'attributes'].forEach(name => {
        context.dispatch(
          `project/data/${name}/updateMentions`,
          {
            id,
            type: 'board',
            name: props.name,
            label: props.name,
            apply: false,
          },
          { root: true }
        );
      });

      // Also update the clipboard (no patch created)
      context.dispatch(
        'application/clipboard/updateMentions',
        { id, type: 'board', name: props.name },
        { root: true }
      );
    }
    updater.complete({ apply });
  },

  /**
   * Remove a board with specified id from the store
   * @param {String}  id      The id of the board
   * @param {Boolean} setOpen Change the open board automatically in case it gets deleted
   * @param {Boolean} apply   Whether to apply the patch after finishing
   */
  customDelete(context, { id, setOpen, apply }) {
    // Find all the descendants of this board
    const ids = context.getters.getDescendantNodes(id, 'boards', 'children');
    // Add the current board also
    ids.push(id);

    // Remove these from any element linkedBoards
    context.dispatch(
      'project/data/elements/deleteBoards',
      { ids, apply: false },
      { root: true }
    );
    // Remove any mentions in elements, notes, connections, attributes
    ['elements', 'notes', 'connections', 'attributes'].forEach(name => {
      context.dispatch(
        `project/data/${name}/deleteMentions`,
        { ids, type: 'board', apply: false },
        { root: true }
      );
    });

    // Remove any mentions in clipboard
    context.dispatch(
      'application/clipboard/deleteMentions',
      { ids, type: 'board' },
      { root: true }
    );

    // Find all the connections, elements, notes, jumpers, branches of these boards
    let connectionIds = [];
    let elementIds = [];
    let noteIds = [];
    let branchIds = [];
    let jumperIds = [];
    let commentIds = [];
    const childCommentIds = [];
    ids.forEach(boardId => {
      const board = context.getters.getNode(boardId);
      // Add any connections not already there
      if (has(board, 'connections')) {
        connectionIds = connectionIds
          .filter(con => !board.connections.includes(con))
          .concat(board.connections);
      }
      // Add any elements not already there
      if (has(board, 'elements')) {
        elementIds = elementIds
          .filter(el => !board.elements.includes(el))
          .concat(board.elements);
      }
      // Add any notes not already there
      if (has(board, 'notes')) {
        noteIds = noteIds
          .filter(note => !board.notes.includes(note))
          .concat(board.notes);
      }
      // Add any jumpers not already there
      if (has(board, 'jumpers')) {
        jumperIds = jumperIds
          .filter(jumper => !board.jumpers.includes(jumper))
          .concat(board.jumpers);
      }
      // Add any branches not already there
      if (has(board, 'branches')) {
        branchIds = branchIds
          .filter(branch => !board.branches.includes(branch))
          .concat(board.branches);
      }
      // Add any comments and their children
      if (has(board, 'comments')) {
        commentIds = commentIds
          .filter(c => !board.comments.includes(c))
          .concat(board.comments);
      }
    });

    // Find any child comments
    console.log('boards.js: commentIds', commentIds);
    commentIds.forEach(c => {
      const thread = context.rootGetters['project/data/comments/getNode'](c);
      // Find any child comments
      if (thread && has(thread, 'children')) {
        childCommentIds.push(...thread.children);
      }
    });

    // Find any conditions
    const conditionIds = [];
    branchIds.forEach(branch => {
      conditionIds.push(
        ...context.rootGetters['project/data/branches/getConditions'](branch)
      );
    });

    // Clear any jumpers
    elementIds.forEach(elementId => {
      context.dispatch(
        'project/data/jumpers/clearElementJumpers',
        { id: elementId, apply: false },
        { root: true }
      );
    });

    // Remove the connections
    context.dispatch(
      'project/data/connections/deleteMany',
      { ids: connectionIds, apply: false },
      { root: true }
    );

    // Remove the elements
    context.dispatch(
      'project/data/elements/deleteMany',
      { ids: elementIds, apply: false },
      { root: true }
    );

    // Remove the notes
    context.dispatch(
      'project/data/notes/deleteMany',
      { ids: noteIds, apply: false },
      { root: true }
    );

    // Remove the jumpers
    context.dispatch(
      'project/data/jumpers/deleteMany',
      { ids: jumperIds, apply: false },
      { root: true }
    );

    // Remove the branches
    context.dispatch(
      'project/data/branches/deleteMany',
      { ids: branchIds, apply: false },
      { root: true }
    );

    // Remove the conditions
    context.dispatch(
      'project/data/conditions/deleteMany',
      { ids: conditionIds, apply: false },
      { root: true }
    );

    // Remove the child comments (do not delete their parent references)
    context.dispatch(
      'project/data/comments/deleteMany',
      { ids: childCommentIds, apply: false, deleteReference: false },
      { root: true }
    );
    // Delete the main comments (threads)
    context.dispatch(
      'project/data/comments/deleteMany',
      { ids: commentIds, apply: false },
      { root: true }
    );
    // If a deleted comment is open close it
    const open = context.rootGetters['project/config/comments/getOpen'];
    if (commentIds.includes(open)) {
      context.dispatch(
        'project/config/comments/setOpen',
        { id: null, apply: false },
        { root: true }
      );
    }

    // Deselect all boards
    ids.forEach(boardId => {
      context.dispatch(
        'project/config/boards/deselect',
        { id: boardId, apply: false },
        { root: true }
      );
    });

    // Remove the actual objects (not their references)
    ids
      .filter(boardId => boardId !== id)
      .forEach(boardId => {
        context.dispatch('delete', {
          id: boardId,
          apply: false,
          deleteReference: false,
        });
      });

    // Remove the initial board object and its reference
    context.dispatch('delete', { id, apply: false });

    updater.complete({ apply });
  },

  /**
   * Duplicate a board with specified id from the store
   * @param {String}  id      The id of the board
   * @param {Boolean} apply   Whether to apply the patch after finishing
   */
  async duplicate(context, { id, apply }) {
    console.log('Duplicating board with id:', id);
    const board = context.getters.getNode(id);
    console.log('Board data:', board);
    // Find the parent board
    const container = context.getters.getContainerNode(
      id,
      'boards',
      'children'
    );
    console.log('Parent board is:', container);
    // Find the index in the parent
    const tIndex = context.getters.getIndex(id, 'boards', 'children');
    console.log('The index in the parent board is:', tIndex);
    // Create a new board
    const clonedBoardId = await context.dispatch('customCreate', {
      board: boardTypes.newItem,
      props: { name: `${board.name} copy` },
      tId: container.id,
      tIndex,
      tExpand: true,
      apply: true,
    });
    console.log('The new board id is:', clonedBoardId);

    const hash = {};
    // Duplicate its elements
    const elements = _.cloneDeep(
      context.rootGetters['project/data/elements/getNodes'](board.elements)
    );
    const elementNodes = elements.map(element => {
      hash[element.id] = uuidv4();
      element.id = hash[element.id];
      return element;
    });

    // Duplicate its jumpers
    const jumpers = _.cloneDeep(
      context.rootGetters['project/data/jumpers/getNodes'](board.jumpers)
    );
    const jumperNodes = jumpers.map(jumper => {
      hash[jumper.id] = uuidv4();
      jumper.id = hash[jumper.id];
      // In case the attached element is in the board, link to its copy
      if (hash[jumper.elementId]) {
        jumper.elementId = hash[jumper.elementId];
      }
      return jumper;
    });

    // Duplicate its conditions
    const cIds = board.branches.flatMap(branchId =>
      context.rootGetters['project/data/branches/getConditions'](branchId)
    );
    const conditions = _.cloneDeep(
      context.rootGetters['project/data/conditions/getNodes'](cIds)
    );
    const conditionNodes = conditions.map(condition => {
      hash[condition.id] = uuidv4();
      condition.id = hash[condition.id];
      return condition;
    });

    // Duplicate its branches
    const branches = _.cloneDeep(
      context.rootGetters['project/data/branches/getNodes'](board.branches)
    );
    const branchNodes = branches.map(branch => {
      hash[branch.id] = uuidv4();
      branch.id = hash[branch.id];

      // Parse the branch conditions
      const {
        ifCondition,
        elseIfConditions,
        elseCondition,
      } = branch.conditions;

      if (ifCondition) {
        branch.conditions.ifCondition = hash[ifCondition];
      }

      if (elseIfConditions) {
        branch.conditions.elseIfConditions = elseIfConditions.map(
          elseIfCondition => hash[elseIfCondition]
        );
      }

      if (elseCondition) {
        branch.conditions.elseCondition = hash[elseCondition];
      }

      return branch;
    });

    // Duplicate its notes
    const notes = _.cloneDeep(
      context.rootGetters['project/data/notes/getNodes'](board.notes)
    );
    const noteNodes = notes.map(note => {
      hash[note.id] = uuidv4();
      note.id = hash[note.id];
      return note;
    });

    // Duplicate its connections
    const connections = _.cloneDeep(
      context.rootGetters['project/data/connections/getNodes'](
        board.connections
      )
    );
    const connectionNodes = connections.map(connection => {
      hash[connection.id] = uuidv4();
      connection.id = hash[connection.id];
      connection.sourceid = hash[connection.sourceid];
      connection.targetid = hash[connection.targetid];
      return connection;
    });

    // Fix the element outputs, make sure they are valid and replace them with new ones
    elementNodes.forEach(element => {
      element.outputs = element.outputs
        .filter(connectionId => has(hash, connectionId))
        .map(connectionId => hash[connectionId]);
    });

    // Fix the condition outputs, make sure they are valid and replace them with new ones
    conditionNodes.forEach(condition => {
      condition.output = hash[condition.output] || null;
    });

    // Create the new elements
    context.dispatch(
      'project/data/elements/createMany',
      {
        nodes: elementNodes,
        tId: clonedBoardId,
        tIndex: 0,
        apply: false,
      },
      { root: true }
    );

    // Create the new jumpers
    context.dispatch(
      'project/data/jumpers/createMany',
      {
        nodes: jumperNodes,
        tId: clonedBoardId,
        tIndex: 0,
        apply: false,
      },
      { root: true }
    );

    // Create new conditions
    context.dispatch(
      'project/data/conditions/createMany',
      {
        nodes: conditionNodes,
        tId: null,
        tIndex: null,
        apply: false,
      },
      { root: true }
    );

    // Create new branches
    context.dispatch(
      'project/data/branches/createMany',
      {
        nodes: branchNodes,
        tId: clonedBoardId,
        tIndex: 0,
        apply: false,
      },
      { root: true }
    );

    // Create the new notes
    context.dispatch(
      'project/data/notes/createMany',
      {
        nodes: noteNodes,
        tId: clonedBoardId,
        tIndex: 0,
        apply: false,
      },
      { root: true }
    );

    // Create the new connections
    context.dispatch(
      'project/data/connections/createMany',
      {
        nodes: connectionNodes,
        tId: clonedBoardId,
        tIndex: 0,
        apply: false,
      },
      { root: true }
    );

    updater.complete({ apply });
  },
};

const module = {
  namespaced: true,
  state,
  getters,
  mutations: {},
  actions,
};

export default module;
